import { useState, useEffect, useMemo, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { getHoursAsync } from "../../../slices/hour";
import { HourEditDialog } from "./hour_edit_dialog";
import { SimpleHourList } from "./simple-hour-list";
import {
  ClockIcon,
  PlusIcon,
  ChevronRightIcon,
} from "@heroicons/react/24/outline";
import { useApiCache } from "../../../hooks/useApiCache";

export const TimeRegistrationSection = ({
  module,
  moduleId,
  project,
  report,
  /** When `module === "work_order"`, pass the work order record to prefill activity + links in the hour dialog. */
  workOrder = null,
  onRefresh,
  title = "Timeregistrering",
  description = "Registrer timer for denne rapporten",
  className = "",
  /** Increment (e.g. parent `setS(n => n + 1)`) to open "new hour" dialog from outside */
  openRegistrationDialogSignal = 0,
}) => {
  const dispatch = useDispatch();
  const { getCachedData, setCachedData } = useApiCache();
  const lastOpenSignalRef = useRef(0);
  const [timeRegistrationDialogOpen, setTimeRegistrationDialogOpen] =
    useState(false);
  const [selectedHourId, setSelectedHourId] = useState(null);
  const [hoursExpanded, setHoursExpanded] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  
  // Determine prefill based on module
  const getPrefill = useMemo(() => {
    const reportProject =
      project?.id
        ? project
        : report?.project?.id
          ? report.project
          : null;
    const base = {
      project_id:
        reportProject?.id ??
        report?.project_id ??
        null,
      project: reportProject,
      date: new Date().toISOString().substring(0, 10),
    };

    switch (module) {
      case "report":
        return { ...base, report_id: moduleId };
      case "work_order": {
        const paId =
          workOrder?.project_activity_id ??
          workOrder?.project_activity?.id ??
          null;
        return {
          ...base,
          project_id:
            base.project_id ??
            workOrder?.project_id ??
            workOrder?.project?.id ??
            null,
          project:
            project ??
            workOrder?.project ??
            null,
          work_order_id: moduleId,
          project_activity_id: paId,
          project_activity: workOrder?.project_activity ?? null,
        };
      }
      case "project":
        return { ...base, project_id: moduleId };
      default:
        return base;
    }
  }, [
    module,
    moduleId,
    project,
    report?.project,
    report?.project_id,
    workOrder?.project_id,
    workOrder?.project?.id,
    workOrder?.project_activity_id,
    workOrder?.project_activity,
  ]);

  const controller = useMemo(() => ({
    query: "",
    page: currentPage,
    per_page: 10,
    sortBy: "created_at",
    sort: "desc",
    filters: [{ name: module.toUpperCase(), value: moduleId }],
    view: null,
    fields:
      "id,time,date,effective_hourly_rate,trips,user,project_activity,project_activity_id",
  }), [module, moduleId, currentPage]);

  const hoursState = useSelector((store) => store.hours);

  // Calculate total registered hours
  const totalRegisteredHours =
    hoursState?.data?.reduce((sum, hour) => {
      return sum + (parseFloat(hour.time) || 0);
    }, 0) || 0;

  // Calculate total labor costs
  const totalLaborCosts =
    hoursState?.data?.reduce((sum, hour) => {
      const time = parseFloat(hour.time) || 0;
      const hourRate = parseFloat(hour.effective_hourly_rate) || 500;
      return sum + time * hourRate;
    }, 0) || 0;

  // Calculate total car allowance trips
  const totalCarAllowanceTrips =
    hoursState?.data?.reduce((sum, hour) => {
      return sum + (hour.trips?.length || 0);
    }, 0) || 0;

  useEffect(() => {
    if (moduleId) {
      const cacheKey = `hours_${module}_${moduleId}_${currentPage}_${controller.fields}`;
      const cachedData = getCachedData(cacheKey);
      
      
      if (cachedData) {
        // If we have cached data, we don't need to dispatch again
        // The Redux store should already have the data
        return;
      }
      
      dispatch(getHoursAsync(controller)).then((result) => {
        // Cache the result if it was successful
        if (result.payload && result.payload.data) {
          setCachedData(cacheKey, result.payload.data);
        }
      }).catch((error) => {
        console.error(`❌ TimeRegistrationSection - getHoursAsync failed:`, error);
      });
    }
  }, [moduleId, module, currentPage, dispatch]);

  useEffect(() => {
    if (
      typeof openRegistrationDialogSignal !== "number" ||
      openRegistrationDialogSignal < 1
    ) {
      return;
    }
    if (openRegistrationDialogSignal <= lastOpenSignalRef.current) {
      return;
    }
    lastOpenSignalRef.current = openRegistrationDialogSignal;
    setSelectedHourId(null);
    setTimeRegistrationDialogOpen(true);
  }, [openRegistrationDialogSignal]);

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleRefresh = () => {
    dispatch(getHoursAsync(controller));
    if (onRefresh) {
      onRefresh();
    }
  };

  const handleEditHour = (hour) => {
    console.log('handleEditHour called with:', hour);
    if (hour?.id) {
      setSelectedHourId(hour.id);
      setTimeRegistrationDialogOpen(true);
    } else {
      console.warn('handleEditHour: hour.id is missing', hour);
    }
  };

  const handleCloseDialog = () => {
    setTimeRegistrationDialogOpen(false);
    setSelectedHourId(null);
  };

  const handleHourSaved = (saved) => {
    handleRefresh();
    handleCloseDialog();
  };

  const handleHourDeleted = () => {
    handleRefresh();
    handleCloseDialog();
  };

  return (
    <>
      <div className={`shadow sm:overflow-hidden sm:rounded-md ${className}`}>
        <div className="space-y-6 bg-white py-6 px-4 sm:p-6">
          <div
            className="flex items-center justify-between cursor-pointer"
            onClick={() => setHoursExpanded(!hoursExpanded)}
          >
            <div className="flex items-center gap-4">
              <div className="flex-shrink-0">
                <ClockIcon className="h-12 w-12 text-teal-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-medium leading-6 text-gray-900">
                  {title}
                </h3>
                <div className="mt-2">
                  <p className="text-sm text-gray-500">{description}</p>
                  <div className="mt-2 flex items-center gap-4">
                    <span className="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-800">
                      <PlusIcon className="h-3 w-3 mr-1" />
                      {totalRegisteredHours.toFixed(1)} timer registrert
                    </span>
                    {totalLaborCosts > 0 && (
                      <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">
                        kr {totalLaborCosts.toLocaleString()}
                      </span>
                    )}
                    {totalCarAllowanceTrips > 0 && (
                      <span className="inline-flex items-center rounded-full bg-purple-100 px-2.5 py-0.5 text-xs font-medium text-purple-800">
                        {totalCarAllowanceTrips} bilgodtgjørelse
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedHourId(null);
                  setTimeRegistrationDialogOpen(true);
                }}
                className="inline-flex items-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
              >
                <PlusIcon className="h-4 w-4 mr-1" />
                Legg til timer
              </button>
              <ChevronRightIcon
                className={`h-5 w-5 text-gray-400 transition-transform ${
                  hoursExpanded ? "rotate-90" : ""
                }`}
              />
            </div>
          </div>

          {hoursExpanded && (
            <div className="border-t border-gray-200 pt-4">
              <SimpleHourList
                hours={hoursState?.data || []}
                loading={hoursState?.isLoading}
                onPageChange={handlePageChange}
                page={controller?.page}
                totalItemsCount={hoursState?.totalCount}
                onRefresh={handleRefresh}
                onItemSelect={handleEditHour}
              />
            </div>
          )}
        </div>
      </div>

      <HourEditDialog
        open={timeRegistrationDialogOpen}
        hourId={selectedHourId}
        prefill={selectedHourId ? null : getPrefill}
        onClose={handleCloseDialog}
        onSaved={handleHourSaved}
        onDeleted={handleHourDeleted}
      />
    </>
  );
};
